import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { AIAssistantWidget } from '@amzn/orion-components';
import { SparkleIcon } from '@amzn/orion-components/icons';
import { getAIAssistantConfig } from 'src/config/aiAssistantConfig';
import { useUserAlias } from 'src/common/hooks/useUserAlias';
import {
  fetchFeatureFlags,
  isFeatureEnabled,
  FeatureFlag,
  FeatureFlagMap,
  defaultFlags,
} from 'src/config/featureFlags';

interface ChatWidgetContextValue {
  openChat: () => void;
  isEnabled: boolean;
}

const ChatWidgetContext = createContext<ChatWidgetContextValue>({
  openChat: () => {},
  isEnabled: false,
});

export const useChatWidget = () => useContext(ChatWidgetContext);

export const ChatWidgetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlagMap>(defaultFlags);
  const [isChatOpen, setIsChatOpen] = useState(
    () => sessionStorage.getItem('cia-chat-open') !== 'false'
  );
  const { alias } = useUserAlias();

  useEffect(() => {
    fetchFeatureFlags().then(setFlags);
  }, []);

  const isEnabled = isFeatureEnabled(flags, FeatureFlag.AI_ASSISTANT);

  const getAuthToken = useCallback(async () => {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  }, []);

  const handleClose = useCallback(() => {
    setIsChatOpen(false);
    sessionStorage.setItem('cia-chat-open', 'false');
  }, []);
  const openChat = useCallback(() => {
    setIsChatOpen(true);
    sessionStorage.setItem('cia-chat-open', 'true');
  }, []);

  const config = useMemo(
    () => ({
      ...getAIAssistantConfig(),
      getAuthToken,
      userAlias: alias,
      visibleToolbarActions: ['new-chat' as const],
    }),
    [getAuthToken, alias]
  );

  const contextValue = useMemo(() => ({ openChat, isEnabled }), [openChat, isEnabled]);

  return (
    <ChatWidgetContext.Provider value={contextValue}>
      <div style={{ display: 'flex', height: '100%', width: '100%' }}>
        <div style={{ flex: 1, minWidth: 0, marginRight: isChatOpen && isEnabled ? 480 : 0 }}>
          {children}
        </div>
        {isEnabled && (
          <div data-react-aria-top-layer style={{ display: 'contents' }}>
            <AIAssistantWidget
              config={config}
              isOpen={isChatOpen}
              onClose={handleClose}
              theme="light"
            />
          </div>
        )}
        {isEnabled && !isChatOpen && (
          <button
            onClick={openChat}
            aria-label="Open AI Assistant"
            className="ai-fab ai-fab--light ai-fab--entry"
            onMouseEnter={(e) => {
              const svg = e.currentTarget.querySelector('.ai-fab__icon svg') as HTMLElement;
              if (svg) svg.style.animation = 'iconHoverSpin 0.6s ease-out forwards';
            }}
            onMouseLeave={(e) => {
              const svg = e.currentTarget.querySelector('.ai-fab__icon svg') as HTMLElement;
              if (svg) svg.style.animation = 'iconBreathing 3s ease-in-out infinite';
            }}
          >
            <span className="ai-fab__shimmer" />
            <span className="ai-fab__icon">
              <SparkleIcon size={28} weight="fill" />
            </span>
          </button>
        )}
      </div>
    </ChatWidgetContext.Provider>
  );
};
